'use client'
import {
  useGetContientswithLangQuery,
  useGetCountriesWithLangQuery,
} from '@/app/globalRedux/api/constantSlice'
import { AppDispatch, RootState } from '@/app/globalRedux/store'
import { useTranslation } from '@/app/i18n/client'
import { OptionsType } from '@/app/types/constantDataType'
import { countryType } from '@/app/types/countryType'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import SingleSelect from 'react-select'
import GroupedSingleSelect from '@/components/ui/select/GroupedSingleSelect'
import { useDispatch, useSelector } from 'react-redux'
import { setGuestNewsForm } from '@/app/globalRedux/guestNewsForm/GuestNewsFormSlice'
import {
  colourStyles,
  formatReactSingleSelectData,
  INITIAL_SELECT_VALUE,
} from '@/components/ui/select/selectStyle'
import { GuestNewsFormUtils } from '@/app/utils/GuestNewsForm'
import { CustomCheckIconOption } from '@/components/ui/select/MultiSelectComponents'
import ReactSingleSelect from '@/components/ui/ReactSingleSelect'
import { NAME_TITLES } from '@/lib/utils'
import { TextField, Autocomplete } from '@mui/material'
import { CustomPopper } from '@/app/[lng]/components/EditMyFructidor/basicInfo'
import { useEffect } from 'react'
import { getCuratedLanguageList } from '@/services/generalDataServices'
import GuestNewsTabs from './GuestNewsTabs'

const GuestNewsFormPage = () => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()
  const GuestNewsForm = useSelector((state: RootState) => state.GuestNewsForm)
  const { curatedLanguages } = useSelector(
    (state: RootState) => state.constantData
  )
  const countriesList = useSelector(
    (state: RootState) => state.constantData.countriesWithOutAllCountry
  )
  const validate = new GuestNewsFormUtils(GuestNewsForm, t)
  const { data: countryList } = useGetCountriesWithLangQuery<{
    data: countryType[]
  }>(
    {
      language: GuestNewsForm?.language?.other?.locale,
    },
    { skip: GuestNewsForm?.language?.id === -1 }
  )

  const { data: continentList = [] } = useGetContientswithLangQuery<{
    data: OptionsType[]
  }>(
    {
      locale: GuestNewsForm?.language?.other?.locale,
    },
    { skip: GuestNewsForm?.language?.id === -1 }
  )

  const isDisabledForLanguage = () => {
    return GuestNewsForm?.language?.id == -1
  }
  useEffect(() => {
    if (curatedLanguages?.length == 0) {
      dispatch(getCuratedLanguageList())
    }
  }, [])
  return (
    <div className="flex flex-col pt-5 gap-y-7 px-8 text-sm">
      <div className="grid desktop:grid-cols-2 justify-between items-start gap-8 mobile:grid-cols-1">
        <div className="grid w-full items-start gap-3 relative">
          <Label htmlFor="language" className="required">
            {t('Languages')}
          </Label>
          <SingleSelect
            options={formatReactSingleSelectData(curatedLanguages)}
            onChange={(option: any) => {
              dispatch(
                setGuestNewsForm({
                  ...GuestNewsForm,
                  language: option,
                  country: INITIAL_SELECT_VALUE,
                })
              )
            }}
            value={
              GuestNewsForm?.language?.id === -1
                ? null
                : GuestNewsForm?.language
            }
            placeholder={t('Select Language')}
            styles={colourStyles}
            menuPlacement="auto"
          />
          {validate?.validateField('language')?.message}
        </div>
        <div className="grid w-full items-start gap-3 relative">
          <Label htmlFor="country" className="required">
            {t('Country')}
          </Label>
          <div
            className={
              isDisabledForLanguage() ? 'cursor-not-allowed' : 'cursor-pointer'
            }
          >
            <GroupedSingleSelect
              id="country"
              isDisabled={isDisabledForLanguage()}
              value={GuestNewsForm?.origin}
              options={[
                {
                  label: 'Continents',
                  options: continentList?.map((item) => ({
                    id: item?.id,
                    value: item?.name,
                    label: item?.name,
                    other: { origin_type: 'Region' },
                  })),
                },
                {
                  label: 'Country',
                  options: countryList?.map((item) => ({
                    id: item?.id,
                    value: item?.name,
                    label: item?.name,
                    other: { origin_type: 'Country' },
                  })),
                },
              ]}
              placeholder={t(
                isDisabledForLanguage()
                  ? 'Choose a language first'
                  : 'Select a Origin'
              )}
              setValue={(value) => {
                value &&
                  dispatch(
                    setGuestNewsForm({
                      origin: value,
                    })
                  )
              }}
              components={{
                Option: (props: any) => (
                  <CustomCheckIconOption
                    {...props}
                    className={'!text-[#1976d2]'}
                  />
                ),
              }}
            />
          </div>
          {validate?.validateField('origin')?.message}
        </div>
      </div>

      <div className=" grid desktop:grid-cols-2 justify-between items-start gap-8 mobile:grid-cols-1">
        <div className="flex flex-col w-full gap-3 relative">
          {/* Title */}
          <div className="flex justify-between items-start gap-3 w-full">
            <div className="flex flex-col w-[20%] gap-3 ">
              <Label className="required" htmlFor="name-title">
                {t('Title')}
              </Label>
              <ReactSingleSelect
                id={'title'}
                placeholder={t('Title')}
                value={GuestNewsForm?.honorific}
                options={NAME_TITLES?.map((title: string, index: number) => ({
                  id: index,
                  label: title,
                  value: title,
                }))}
                setValue={(value) => {
                  dispatch(
                    setGuestNewsForm({
                      honorific: value,
                    })
                  )
                }}
              />
            </div>

            {/* First Name */}
            <div className="flex flex-col w-[40%] gap-3 relative">
              <Label className="required" htmlFor="first-name">
                {t('First Name')}
              </Label>
              <Input
                type="text"
                placeholder={t('Enter Your FirstName')}
                value={GuestNewsForm?.firstName ?? ''}
                onChange={(event) => {
                  dispatch(
                    setGuestNewsForm({
                      firstName: event?.target?.value,
                    })
                  )
                }}
              />
            </div>

            {/* Last Name */}
            <div className="flex flex-col w-[40%] gap-3 relative">
              <Label className="required" htmlFor="last-name">
                {t('Last Name')}
              </Label>
              <Input
                type="text"
                placeholder={t('Enter Your LastName')}
                value={GuestNewsForm?.lastName ?? ''}
                onChange={(event) => {
                  dispatch(
                    setGuestNewsForm({
                      lastName: event?.target?.value,
                    })
                  )
                }}
              />
            </div>
          </div>
          {validate?.validateField('name')?.message}
        </div>
        <div className="grid w-full items-start gap-3 relative">
          <Label className="required" htmlFor="phone">
            {t('Phone')}
          </Label>
          <div className="grid grid-cols-10">
            <div className="col-span-2">
              <Autocomplete
                disableClearable
                disablePortal
                forcePopupIcon={false}
                id="country-phone-code"
                PopperComponent={CustomPopper}
                options={countriesList}
                getOptionLabel={(option) => `${option.dial_code}`}
                filterOptions={(options, state) =>
                  options.filter(
                    (option) =>
                      option.name
                        ?.toLowerCase()
                        .includes(state.inputValue?.toLowerCase()) ||
                      (option?.dial_code &&
                        option.dial_code.includes(state.inputValue))
                  )
                }
                sx={{
                  ' .MuiOutlinedInput-notchedOutline': {
                    border: '2px solid #e0e0e0 !important',
                    borderTopRightRadius: '0px !important',
                    borderBottomRightRadius: '0px !important',
                  },
                  '.MuiAutocomplete-input': {
                    textAlign: 'center',
                  },
                }}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder={t('Code')}
                    />
                  )
                }}
                onChange={(event, value) => {
                  dispatch(setGuestNewsForm({ phoneCodeId: value }))
                }}
                value={GuestNewsForm?.phoneCodeId}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={`${option.name} ${option.dial_code}`}>
                      <span className="text-[14px]">{`${option.dial_code} (${option.name})`}</span>
                    </li>
                  )
                }}
              />
            </div>

            <Input
              className="col-span-8 !border-l-0 rounded-l-none outline-none"
              id="phone"
              placeholder={t('Phone')}
              type="text"
              maxLength={15}
              value={GuestNewsForm?.phone}
              onChange={(e) => {
                const value = e.target.value
                if (Number(value) || !value) {
                  dispatch(setGuestNewsForm({ phone: e.target.value }))
                }
              }}
            />
          </div>
          {validate?.validateField('phone')?.message}
        </div>
      </div>

      <div className="grid desktop:grid-cols-2 justify-between items-start gap-8 mobile:grid-cols-1">
        <div className="grid w-full items-start gap-3 relative">
          {/* toggle to sent news */}
        </div>
        <div className="grid w-full items-start gap-3 relative">
          <Label htmlFor="Company" className="required">
            {t('Company')}
          </Label>
          <Input
            type="text"
            placeholder={t('Enter Company Name')}
            value={GuestNewsForm?.company ?? ''}
            onChange={(event) => {
              dispatch(
                setGuestNewsForm({
                  company: event?.target?.value,
                })
              )
            }}
          />
          {validate?.validateField('company')?.message}
        </div>
      </div>
      <GuestNewsTabs />
    </div>
  )
}

export default GuestNewsFormPage
